<template>
	<div class="pdf-container">
		<!-- <div v-if="loading" class="loading">
			<p>加载中...</p>
		</div> -->
		<div class="pdf-toolbar">
			<el-button size="mini" @click="prevPage" :disabled="currentPage <= 1">上一页</el-button>
			<span>第 {{ currentPage }} / {{ totalPages }} 页</span>
			<el-button size="mini" @click="nextPage" :disabled="currentPage >= totalPages">下一页</el-button>
			<select v-model="renderScale" @change="handleRenderScaleChange" class="render-scale-select">
				<option :value="1">标准清晰度</option>
				<option :value="2">高清(2x)</option>
				<option :value="3">超清(3x)</option>
				<option :value="4">超高清(4x)</option>
			</select>
			<el-button size="mini" @click="zoomOut" :disabled="scale <= 0.5">缩小(-)</el-button>
			<el-button size="mini" @click="zoomIn" :disabled="scale >= 5">放大(+)</el-button>
			<el-button size="mini" @click="zoomTo(1)">重置缩放</el-button>
			<el-button size="mini" @click="rotateCounterClockwise" title="逆时针旋转90度">↺ 逆时针旋转</el-button>
			<el-button size="mini" @click="rotateClockwise" title="顺时针旋转90度">↻ 顺时针旋转</el-button>
			<el-button
					size="mini"
					@click="toggleDarkMode"
					:type="isDarkMode ? 'primary' : ''"
					title="切换深色/浅色模式"
			>
				{{ isDarkMode ? '浅色模式' : '深色模式' }}
			</el-button>
			<el-button size="mini" @click="toggleDragMode" :type="isDragMode ? 'primary' : ''">
				{{ isDragMode ? '退出拖曳' : '开启拖曳' }}
			</el-button>
			<span class="zoom-info">当前缩放: {{ Math.round(scale * 100) }}%</span>
			<span class="rotation-info" v-if="rotation !== 0">
				旋转角度: {{ rotation }}°
			</span>
		</div>
		<div v-if="error" class="error">{{ error }}</div>

		<!-- 关键：去掉 passive，必须用原生捕获式监听 -->
		<div class="pdf-content" :class="{ 'drag-mode': isDragMode, 'dark-mode': isDarkMode }">
			<div class="pdf-wrapper"
			     ref="pdfContainer"
			     @mousedown="startDrag"
			     @mousemove="onDrag"
			     @mouseup="stopDrag"
			     @mouseleave="stopDrag"></div>
		</div>
	</div>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry'

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker

export default {
	props: {
		pdfUrl: { type: String, required: true },
		minX: { type: Number, required: true },
		minY: { type: Number, required: true },
		maxX: { type: Number, required: true },
		maxY: { type: Number, required: true },
		coordinateType: { type: String, default: 'absolute' },
		highlightColor: { type: String, default: 'rgba(255, 255, 0, 0.3)' },
		pageNum: { type: Number, default: 1 },
		coordinatesInPixels: { type: Boolean, default: false }
	},
	data() {
		return {
			loading: false,
			error: '',
			pdfDoc: null,
			currentPage: 1,
			totalPages: 0,
			scale: 1.0,
			pageCanvases: {},
			pageViewports: {},
			pageDimensions: {},
			reloadTimer: null,
			isDragMode: false,
			isDragging: false,
			dragStartX: 0,
			dragStartY: 0,
			scrollLeft: 0,
			scrollTop: 0,
			renderTasks: {},
			zoomTimer: null,
			renderScale: 1, // 渲染倍率（1=标准, 2=高清, 3=超高清）
			rotation: 0, // 当前旋转角度（0, 90, 180, 270）
			isDarkMode: false // 深色模式开关
		}
	},
	mounted() {
		this.loadPdf();
		// ✅ 核心修复：原生捕获级监听滚轮，强制禁止浏览器默认行为
		const el = this.$refs.pdfContainer.parentElement;
		el.addEventListener('wheel', this.handleWheel, { passive: false});
		el.addEventListener('mousedown',this.boundMouseDown)
		// el.addEventListener('wheel', this.handleWheel, { passive: false, capture: true });
		

	},
	beforeUnmount() {
		clearTimeout(this.reloadTimer);
		clearTimeout(this.zoomTimer);
		Object.values(this.renderTasks).forEach(task => {
			if (task) task.cancel().catch(() => {});
		});

		// 清理监听
		const el = this.$refs.pdfContainer?.parentElement;
		if (el) {
			el.removeEventListener('wheel', this.handleWheel);
			el.removeEventListener('mousedown',this.boundMouseDown);
		}
		this.handleClose();
	},
	watch: {
		pdfReloadTrigger: {
			handler() {
				this.handleClose();
				clearTimeout(this.reloadTimer);
				this.reloadTimer = setTimeout(() => this.loadPdf(), 100);
			}
		},
		isDarkMode(newVal) {
			this.$nextTick(() => {
				this.applyDarkMode();
			});
		}
	},
	computed: {
		pdfReloadTrigger() {
			return JSON.stringify([
				this.pdfUrl, this.pageNum,
				this.minX, this.minY, this.maxX, this.maxY,
				this.coordinatesInPixels
			]);
		}
	},
	methods: {
		toggleDragMode() {
			this.isDragMode = !this.isDragMode;
		},
		startDrag(e) {
			if (!this.isDragMode) return;
			e.preventDefault();
			this.isDragging = true;
			const container = this.$refs.pdfContainer.parentElement;
			this.dragStartX = e.clientX;
			this.dragStartY = e.clientY;
			this.scrollLeft = container.scrollLeft;
			this.scrollTop = container.scrollTop;
			container.style.cursor = 'grabbing';
		},
		onDrag(e) {
			if (!this.isDragging || !this.isDragMode) return;
			e.preventDefault();
			const container = this.$refs.pdfContainer.parentElement;
			container.scrollLeft = this.scrollLeft - (e.clientX - this.dragStartX);
			container.scrollTop = this.scrollTop - (e.clientY - this.dragStartY);
		},
		stopDrag() {
			if (!this.isDragMode) return;
			this.isDragging = false;
			const container = this.$refs.pdfContainer.parentElement;
			if (container) container.style.cursor = 'grab';
		},

		// ✅ 最终版：100% 禁止浏览器默认 Ctrl+滚轮缩放
		// handleWheel(e) {
		// 	if (e.ctrlKey || e.metaKey) {
		// 		e.preventDefault();
		// 		e.stopPropagation();
		// 		clearTimeout(this.zoomTimer);
		// 		this.zoomTimer = setTimeout(() => {
		// 			e.deltaY < 0 ? this.zoomIn() : this.zoomOut();
		// 		}, 20);
		// 	}
		// },
		//最终版：100% 禁止浏览器默认 滚轮缩放
		handleWheel(e) {
			console.log('滚动滑轮了',e)
			e.preventDefault();
				if (e.deltaY < 0) {
					this.zoomIn();      // 向上滚 → 放大
				} else {
					this.zoomOut();     // 向下滚 → 缩小
				}

		},
		boundMouseDown(e) {
			if (e.button !== 0) return;
			this.isDragMode = true;
		},
		
		


		handleClose() {
			clearTimeout(this.reloadTimer);
			clearTimeout(this.zoomTimer);
			Object.values(this.renderTasks).forEach(t => t?.cancel().catch(() => {}));
			this.pdfDoc = null;
			this.pageCanvases = {};
			this.pageViewports = {};
			this.pageDimensions = {};
			this.renderTasks = {};
			if (this.$refs.pdfContainer) this.$refs.pdfContainer.innerHTML = '';
			this.currentPage = 1;
			this.scale = 1.0;
			this.error = '';
		},
		async loadPdf() {
			if (!this.pdfUrl) return;
			this.loading = true;
			this.error = '';
			try {
				const pdfDoc = await pdfjsLib.getDocument({
					url: this.pdfUrl,
					cMapUrl: 'https://unpkg.com/pdfjs-dist@2.16.105/cmaps/',
					cMapPacked: true
				}).promise;
				this.pdfDoc = pdfDoc;
				this.totalPages = pdfDoc.numPages;
				await this.getPageDimensions(this.pageNum);
				this.renderPage(this.pageNum);
				this.currentPage = this.pageNum;
			} catch (err) {
				this.error = '加载PDF失败';
			} finally {
				this.loading = false;
			}
		},
		async getPageDimensions(pageNum) {
			if (!this.pdfDoc || this.pageDimensions[pageNum]) return;
			const page = await this.pdfDoc.getPage(pageNum);
			// console.log(`第 ${pageNum} 页的原始旋转角度: ${page.rotate}`);
			this.rotation = page.rotate; // 初始化旋转角度为 PDF 原始旋转角度
			const vp = page.getViewport({ scale: 1,rotation: this.rotation });
			this.pageDimensions[pageNum] = { width: vp.width, height: vp.height };
		},
		async renderPage(pageNum) {
			if (!this.pdfDoc || pageNum < 1 || pageNum > this.totalPages) return;
			try {
				this.loading = true;
				const page = await this.pdfDoc.getPage(pageNum);
				// 基础缩放 + 旋转 的显示尺寸
				const baseViewport = page.getViewport({ scale: this.scale, rotation: this.rotation });
				// 高清渲染的物理尺寸 = 显示尺寸 * renderScale
				const renderViewport = page.getViewport({
					scale: this.scale * this.renderScale,
					rotation: this.rotation,
					antialias: true
				});
				this.pageViewports[pageNum] = baseViewport;

				let canvas = this.pageCanvases[pageNum];
				if (!canvas) {
					canvas = document.createElement('canvas');
					canvas.className = 'pdf-page';
					this.pageCanvases[pageNum] = canvas;
					this.$refs.pdfContainer.appendChild(canvas);
				}
				const ctx = canvas.getContext('2d');
				// 设置 canvas 物理尺寸（高清）
				canvas.width = renderViewport.width;
				canvas.height = renderViewport.height;
				// 设置 canvas 显示尺寸（避免变形）
				canvas.style.width = `${baseViewport.width}px`;
				canvas.style.height = `${baseViewport.height}px`;

				if (this.renderTasks[pageNum]) this.renderTasks[pageNum].cancel();
				const task = page.render({ canvasContext: ctx, viewport: renderViewport });
				this.renderTasks[pageNum] = task;
				await task.promise;
				delete this.renderTasks[pageNum];

				this.drawHighlight(canvas, pageNum);
				// 如果当前是深色模式，新渲染的 canvas 也要应用滤镜
				if (this.isDarkMode) {
					canvas.style.filter = 'invert(1) hue-rotate(180deg)';
				}
				this.currentPage = pageNum;
			} catch (e) {} finally {
				this.loading = false;
			}
		},
		drawHighlight(canvas, pageNum) {
			const vp = this.pageViewports[pageNum];
			const dims = this.pageDimensions[pageNum];
			if (!vp || !dims) return;
			const ctx = canvas.getContext('2d');
			ctx.save();

			let x, y, w, h;
			if (this.coordinateType === 'relative') {
				x = this.minX * canvas.width;
				y = this.minY * canvas.height;
				w = (this.maxX - this.minX) * canvas.width;
				h = (this.maxY - this.minY) * canvas.height;
			} else {
				let [mx, my, Mx, My] = [this.minX, this.minY, this.maxX, this.maxY];
				if (this.coordinatesInPixels) {
					mx *= 72/96; my *= 72/96; Mx *= 72/96; My *= 72/96;
				}
				x = mx * this.scale;
				y = (dims.height - My) * this.scale;
				w = (Mx - mx) * this.scale;
				h = (My - my) * this.scale;
			}
			ctx.fillStyle = this.highlightColor;
			ctx.fillRect(x, y, w, h);
			ctx.strokeStyle = 'rgba(255,0,0,0.8)';
			ctx.lineWidth = 2;
			ctx.strokeRect(x, y, w, h);
			ctx.restore();
		},
		prevPage() { this.renderPage(this.currentPage - 1); },
		nextPage() { this.renderPage(this.currentPage + 1); },
		zoomIn() { if (this.scale < 5) { this.scale += 0.1; this.renderPage(this.currentPage); } },
		zoomOut() { if (this.scale > 0.5) { this.scale -= 0.1; this.renderPage(this.currentPage); } },
		zoomTo(scale) { this.scale = scale; this.renderPage(this.currentPage); },
		// 处理渲染倍率变化
		handleRenderScaleChange() {
			if (this.pdfDoc) {
				this.renderPage(this.currentPage);
			}
		},
		// 顺时针旋转90度
		rotateClockwise() {
			this.rotation = (this.rotation + 90) % 360;
			if (this.pdfDoc) {
				this.renderPage(this.currentPage);
			}
		},
		// 逆时针旋转90度
		rotateCounterClockwise() {
			this.rotation = (this.rotation - 90 + 360) % 360;
			if (this.pdfDoc) {
				this.renderPage(this.currentPage);
			}
		},
		// 切换深色模式
		toggleDarkMode() {
			this.isDarkMode = !this.isDarkMode;
			this.applyDarkMode();
		},
		// 应用深色模式效果
		applyDarkMode() {
			// 只对 canvas 应用 invert，不要再对容器应用（避免双重反转抵消）
			const pdfContent = this.$refs.pdfContainer && this.$refs.pdfContainer.parentElement;
			if (!pdfContent) return;

			const canvases = pdfContent.querySelectorAll('canvas');
			if (this.isDarkMode) {
				// 深色模式：仅反转 canvas 颜色
				canvases.forEach(canvas => {
					canvas.style.filter = 'invert(1) hue-rotate(180deg)';
				});
			} else {
				// 浅色模式：恢复正常
				canvases.forEach(canvas => {
					canvas.style.filter = '';
				});
			}
		}
	}
}
</script>

<style scoped>
>>> .el-dialog__header{
	padding:0;
}
>>> .el-dialog__body{
	padding:0;
}
.pdf-container { max-height: 90vh; display: flex; flex-direction: column; }
.loading, .error { text-align: center; padding: 20px; }
.error { color: #f56c6c; }
.pdf-toolbar { margin-bottom: 10px; padding: 5px; background: #f5f7fa; border-radius: 4px; display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
.pdf-content { flex: 1; overflow: auto; position: relative; transition: filter 0.3s ease; }
.pdf-wrapper { display: flex; flex-direction: column; align-items: center; gap: 15px; padding: 10px 0; min-width: min-content; }
.pdf-page { box-shadow: 0 0 8px rgba(0,0,0,0.1); max-width: 100%; }
.pdf-content.drag-mode .pdf-wrapper { cursor: grab; user-select: none; }
.pdf-content.drag-mode .pdf-wrapper:active { cursor: grabbing; }

.render-scale-select {
	padding: 4px 8px;
	border: 1px solid #ddd;
	border-radius: 4px;
	background: #fff;
	cursor: pointer;
	transition: all 0.2s;
	font-size: 12px;
	color: #606266;
}
.render-scale-select:hover {
	border-color: #409eff;
}
.render-scale-select:focus {
	outline: none;
	border-color: #409eff;
	box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

.zoom-info {
	color: #666;
	font-size: 12px;
	margin-left: auto;
}
.rotation-info {
	color: #67c23a;
	font-size: 12px;
	padding: 4px 8px;
	background: rgba(103, 194, 58, 0.1);
	border-radius: 4px;
}
</style>